<template>
  <div class="add-inventory-dialog">
    <!-- Scrollable Content -->
    <div class="dialog-content">
      <v-form ref="form" v-model="valid" lazy-validation>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <v-text-field
            v-model="orderNumber"
            :disabled="isEditModeEnable"
            label="Order Number"
            placeholder="Order number"
            outlined
            dense
            hide-details
          ></v-text-field>
          <v-text-field
            v-model="partName"
            :disabled="isEditModeEnable"
            label="Spare part name"
            placeholder="Spare part name"
            outlined
            dense
            hide-details
            :rules="[(v) => !!v || 'Spare part name is required']"
          ></v-text-field>
          <v-autocomplete
            flat
            :menu-props="{ offsetY: true }"
            v-model="partner"
            label="Search Brand"
            placeholder="Search Brand"
            :items="activePartners"
            @update:search-input="fetchPartners"
            :search-input.sync="searchPartner"
            item-text="partnerName"
            item-value="_id"
            outlined
            dense
            hide-details
            :rules="[(v) => !!v || 'Brand/Partner name is required']"
          ></v-autocomplete>
          <v-text-field
            v-model="partNumber"
            :disabled="isEditModeEnable"
            label="Spare part model number"
            placeholder="Spare part model number"
            outlined
            dense
            hide-details
            :rules="[(v) => !!v || 'Spare part model number is required']"
          ></v-text-field>
          <v-text-field
            v-model="costPricePerUnit"
            label="Cost Price"
            outlined
            dense
            hide-details
          ></v-text-field>
          <v-text-field
            v-model="productPrice"
            label="Selling Price"
            outlined
            dense
            hide-details
          ></v-text-field>
          <v-text-field v-model="tax" label="Taxes" outlined dense hide-details></v-text-field>
          <v-text-field
            v-model="discount"
            label="Discount"
            outlined
            dense
            hide-details
          ></v-text-field>
          <v-text-field
            v-model="splDiscount"
            label="Special Discount"
            outlined
            dense
            hide-details
          ></v-text-field>
          <v-text-field
            v-model="installationCharges"
            label="Installation Charges"
            outlined
            hide-details
            dense
          ></v-text-field>
          <v-text-field
            v-model="minStockThreshold"
            label="Minimum Stock Limit"
            outlined
            dense
            hide-details
          ></v-text-field>
          <v-select
            flat
            :menu-props="{ offsetY: true }"
            v-model="selectedStores"
            :items="activeStores"
            item-text="storeName"
            item-value="_id"
            label="Delivery Stores"
            multiple
            outlined
            dense
            hide-details
            :rules="[(v) => !!v.length || 'Store name is required']"
          ></v-select>
          <v-text-field
            v-for="(item, i) in quantities"
            :key="item.name"
            type="number"
            v-model="quantityInStore[i]"
            @change="(value) => onQuantityChange(i, value)"
            :label="'Qty delivered to ' + item.name"
            outlined
            dense
            hide-details
          ></v-text-field>
          <v-text-field
            type="Total Quantity"
            v-model="calculateTotalQuantity"
            label="Total Quantity"
            outlined
            disabled
            dense
            hide-details
          ></v-text-field>
          <v-file-input
            v-if="!!inventory"
            v-model="productImages"
            prepend-icon="mdi-camera"
            label="Product Image"
            multiple
            hide-details
          ></v-file-input>
        </div>
      </v-form>
    </div>

    <!-- Fixed Footer -->
    <div class="dialog-footer border-t p-4">
      <div v-if="!!inventory" cols="12" class="text-right">
        <v-btn class="rounded-md mr-2 normal-case" @click="cancelUpdate"> Cancel </v-btn>
        <v-btn
          class="bg-[#2A83FF] white--text normal-case"
          :color="$vuetify.theme.currentTheme.primary"
          @click="handleEditInventory"
        >
          Update
        </v-btn>
      </div>
      <div v-else cols="12" class="text-right">
        <v-btn class="rounded-md mr-2 normal-case" @click="cancel" text> Cancel </v-btn>
        <v-btn
          class="bg-[#2A83FF] white--text normal-case"
          :color="$vuetify.theme.currentTheme.primary"
          @click="addInventory"
        >
          <v-icon left>mdi-content-save</v-icon>
          Save
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { sortDropdownOptions } from '@/utils/common'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  props: ['inventory'],
  data() {
    return {
      partName: '',
      orderNumber: '',
      partner: '',
      partNumber: '',
      costPricePerUnit: '',
      productPrice: null,
      minStockThreshold: null,
      tax: null,
      discount: null,
      splDiscount: null,
      installationCharges: null,
      partners: [],
      productImages: [],
      stores: [],
      selectedStores: [],
      quantityInStore: [],
      valid: true
    }
  },
  computed: {
    quantities() {
      return this.selectedStores.map((storeId) => {
        const store = this.stores.find((store) => storeId === store._id) || {}

        return { name: store.storeName, quantity: 0 }
      })
    },
    generateRandomNumber(min, max) {
      return Math.floor(Math.random() * (max - min + 1) + min)
    },
    isEditModeEnable() {
      if (this.$props.inventory) {
        return true
      }
      return false
    },
    activePartners() {
      const partners = this.partners.filter((partner) => partner.partnerStatus !== 'IN_ACTIVE')

      return sortDropdownOptions(partners, 'partnerName')
    },
    activeStores() {
      return this.stores.filter((store) => store.storeStatus !== 'NON_OPERATIONAL')
    },
    calculateTotalQuantity() {
      return this.quantityInStore.reduce((sum, quantity) => sum + Number(quantity), 0)
    }
  },
  mounted() {
    this.fetchPartners()
    this.fetchStores()
    this.populateFields()
  },
  methods: {
    async handleEditInventory() {
      const updatedInventory = this.getInventoryData()
      const isValid = this.$refs.form.validate()
      if (!isValid) {
        this.$toast.error('Please fill all the required fields')
        return
      }
      try {
        const response = await this.$axios.put(
          `/ecom/product/${this.$props.inventory._id}`,
          updatedInventory
        )
        if (this.productImages.length) {
          this.uploadProductImages()
        }
        this.cancelUpdate()
        const errorMessage = response?.data?.message || 'Product updated successfully!!'
        this.$toast.success(errorMessage)
      } catch (error) {
        const errorMessage = error.response.data.message || 'Order failed.'
        this.$toast.error(errorMessage)
      }
    },
    async uploadProductImages() {
      const formData = new FormData()
      formData.append('productId', this.$props.inventory._id)
      for (const image of this.productImages) {
        formData.append('attachments', image)
      }

      try {
        await this.$axios.post(`/ecom/product/images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
      } catch (error) {
        console.error('An error occurred:', error)
        const errorMessage = error?.response?.data?.message || 'Product Image Upload Failed !!'
        this.$toast.error(errorMessage)
      }
    },
    populateFields() {
      if (this.$props.inventory) {
        const inventoryData = this.$props.inventory
        this.partName = inventoryData.productName
        this.partNumber = inventoryData.modelNumber
        this.orderNumber = inventoryData.lastOrderNo
        this.productPrice = inventoryData.productPrice
        this.tax = inventoryData.tax
        this.minStockThreshold = inventoryData.minStockThreshold
        this.discount = inventoryData.discount
        this.splDiscount = inventoryData.splDiscount
        this.installationCharges = inventoryData.installationCharges
        this.partner = inventoryData.partnerDetails?._id
        this.selectedStores =
          inventoryData.inStoreAvailability?.map((item) => item.storeLocation?._id) || []
        this.quantityInStore =
          inventoryData.inStoreAvailability?.map((item) => item.quantityInStock) || []
        this.costPricePerUnit = inventoryData.costPricePerUnit
      }
    },
    onQuantityChange(storeIndx, quantity) {
      this.quantityInStore[storeIndx] = quantity
    },
    async fetchPartners() {
      const { data } = await this.$axios.get(axiosUrl.partners.getPartners, {
        params: {
          limit: 100
        }
      })

      if (data.success) {
        this.partners = data.partners
      }
    },
    async fetchStores() {
      const { data } = await this.$axios.get(axiosUrl.stores.getStores, {})

      if (data.success) {
        this.stores = data.stores
      }
    },
    getInventoryData() {
      const inStoreAvailability = []

      this.selectedStores.forEach((storeId, i) => {
        const store = this.stores.find((store) => store._id === storeId)
        const data = {
          storeLocation: store?._id,
          quantityInStock: Number(this.quantityInStore[i])
        }

        inStoreAvailability.push(data)
      })

      const inventoryData = {
        inStoreAvailability,
        productName: this.partName,
        modelNumber: this.partNumber,
        lastOrderNo: this.orderNumber,
        costPricePerUnit: this.costPricePerUnit,
        productPrice: this.productPrice,
        tax: this.tax,
        discount: this.discount,
        splDiscount: this.splDiscount,
        installationCharges: this.installationCharges,
        minStockThreshold: Number(this.minStockThreshold),
        quantityInStock: this.calculateTotalQuantity,
        partnerDetails: this.partner
      }

      return inventoryData
    },
    async addInventory() {
      const inventoryData = this.getInventoryData()
      const isValid = this.$refs.form.validate()
      if (!isValid) {
        this.$toast.error('Please fill all the required fields')
        return
      }
      try {
        const response = await this.$axios.post('/ecom/product', inventoryData)
        const successMessage = response?.data?.message || 'Product added successfully !!'
        this.$toast.success(successMessage)
        this.cancel()
      } catch (error) {
        const errorMessage = error.response.data.message || 'Order failed.'
        this.$toast.error(errorMessage)
      }
    },
    cancelUpdate() {
      this.$emit('cancelUpdate')
    },

    cancel() {
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.add-inventory-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
}

.dialog-header {
  flex: 0 0 auto;
  padding: 16px;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
}

.dialog-content {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;
}

.dialog-footer {
  flex: 0 0 auto;
  background-color: white;
}
</style>
